<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import { RouterLink, RouterView, useRoute } from "vue-router";

import SiteFooter from "./components/SiteFooter.vue";

/**
 * The shell: the header picture, the dark navigation bar, the white content
 * column and the footer. Everything the old index.html held, unchanged in
 * markup and class names, so the page looks exactly as it did.
 *
 * The footer is a component of its own since it gained the row of account
 * icons: it has a data source now, and this file has none.
 *
 * What is new is <RouterView>: the fragment loader that replaced the contents
 * of <main> by AJAX is gone, and each page is a route with an address of its
 * own.
 */

const NAV_COLLAPSE_ID = "navbarNavAltMarkup";

const navCollapse = ref<HTMLElement | null>(null);
const content = ref<HTMLElement | null>(null);
const route = useRoute();

/**
 * Closes the burger menu.
 *
 * On the old site a click reloaded the fragment underneath an open menu, which
 * nobody noticed because the menu covered nothing. With a router the menu would
 * simply stay open on top of the page that was just opened.
 *
 * Only when it is actually open: on wide screens Bootstrap keeps the element
 * expanded and there is nothing to close.
 */
async function closeMenu(): Promise<void> {
  const element = navCollapse.value;
  if (!element?.classList.contains("show")) {
    return;
  }

  // Imported here rather than at the top of the file: the module registers a
  // document-level handler as it loads, and this component is also rendered in
  // node by the prerender step, where there is no document.
  const { default: Collapse } = await import("bootstrap/js/dist/collapse");
  Collapse.getOrCreateInstance(element, { toggle: false }).hide();
}

/**
 * After a navigation: close the menu, and put the keyboard where the new page
 * is.
 *
 * A full page load used to do the second part by itself. A router does not:
 * without this, a screen reader is told nothing and the keyboard stays on a
 * link that is about to be hidden inside the closing menu - which drops focus
 * to <body> and starts the next Tab at the top of the document.
 */
watch(
  () => route.fullPath,
  async () => {
    await closeMenu();
    await nextTick();
    content.value?.focus();
  },
);

/**
 * Tapping the entry for the page one is already on is a navigation that does
 * not change the address, so the watcher above never fires - and the menu
 * would stay open over the page it just failed to leave.
 */
function onNavClick(): void {
  void closeMenu();
}
</script>

<template>
  <header>
    <img
      class="group"
      src="/images/tubetaenzig-band-gruppenbild.jpg"
      alt="Gruppenbild der Schweizer Ländlermusik-Band tubetänzig aus Laufenburg"
    />
    <nav
      class="navbar bg-dark border-bottom border-body navbar-expand-lg"
      data-bs-theme="dark"
    >
      <div class="container-fluid">
        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="`#${NAV_COLLAPSE_ID}`"
          :aria-controls="NAV_COLLAPSE_ID"
          aria-expanded="false"
          aria-label="Navigation ein- und ausblenden"
        >
          <span class="navbar-toggler-icon"></span>
        </button>
        <div
          :id="NAV_COLLAPSE_ID"
          ref="navCollapse"
          class="collapse navbar-collapse"
        >
          <div class="navbar-nav" @click="onNavClick">
            <RouterLink class="nav-link" active-class="active" to="/">
              Home
            </RouterLink>
            <RouterLink class="nav-link" active-class="active" to="/medien">
              Medien
            </RouterLink>
            <RouterLink class="nav-link" active-class="active" to="/auftritte">
              Auftritte
            </RouterLink>
            <RouterLink class="nav-link" active-class="active" to="/ueber-uns">
              Über uns
            </RouterLink>
            <RouterLink class="nav-link" active-class="active" to="/kontakt">
              Kontakt
            </RouterLink>
          </div>
        </div>
      </div>
    </nav>
  </header>

  <!--
    tabindex="-1" so a navigation can move the keyboard here. It is not a
    control: main:focus in style.css keeps it from drawing a focus ring.
  -->
  <main ref="content" tabindex="-1">
    <RouterView />
  </main>

  <SiteFooter />
</template>
